import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../HandleAPICalls/actions";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import Sidebar from "../../Navigation/Sidebar/Sidebar";
import SubCategoryListComponent from "./component";

const SubCategoryListContainer = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryId } = useParams();
  
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const categoryData = location.state?.categoryData;
  const categoryIdFromParams = categoryId || categoryData?.id;

  const S3_BASE_URL = "https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/";
  const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=400&fit=crop";

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    return `${S3_BASE_URL}${imagePath}`;
  };

  const fetchSubCategories = () => {
    if (!categoryIdFromParams) {
      toastr.error("Error", "Category ID not found");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    props.getDataFromAPI(
      `/partner/api/v1/product-categories/${categoryIdFromParams}/subcategories`,
      "GET",
      undefined,
      (response) => {
        let subCategoriesData = [];

        if (response && Array.isArray(response)) {
          subCategoriesData = response;
        } else if (response?.subcategories && Array.isArray(response.subcategories)) {
          subCategoriesData = response.subcategories;
        } else if (response?.content && Array.isArray(response.content)) {
          subCategoriesData = response.content;
        }

        const formattedSubCategories = subCategoriesData.map(item => ({
          id: item.id,
          name: item.name,
          description: item.description || "",
          image: getFullImageUrl(item.image) || DEFAULT_IMAGE,
          image_url: getFullImageUrl(item.image) || DEFAULT_IMAGE,
          active: item.active === true ? true : false,
          status: item.active === true ? "active" : "inactive",
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        }));

        setSubCategories(formattedSubCategories);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching sub categories:", err);
        setError(err?.message || "Failed to fetch sub categories");
        toastr.error("Failed", "Unable to fetch sub categories");
        setLoading(false);
      }
    );
  };

  const fetchCategoryDetails = () => {
    if (!categoryIdFromParams) return;
    
    props.getDataFromAPI(
      `/partner/api/v1/product-categories/${categoryIdFromParams}`,
      "GET",
      undefined,
      (response) => {
        if (response) {
          setCategory({
            id: response.id,
            name: response.name,
            description: response.description,
            image: getFullImageUrl(response.image) || DEFAULT_IMAGE,
            active: response.active,
          });
        }
      },
      (err) => {
        console.error("Error fetching category details:", err);
      }
    );
  };

  useEffect(() => {
    if (categoryData) {
      setCategory(categoryData);
      fetchSubCategories();
    } else if (categoryIdFromParams) {
      fetchCategoryDetails();
      fetchSubCategories();
    } else {
      setLoading(false);
    }
  }, [categoryIdFromParams]);

  const handleBack = () => {
    navigate("/home/configuration/product-restructure");
  };

  const handleAddSubCategory = () => {
    navigate(`/home/product/categories/subcategories/create/${categoryIdFromParams}`, {
      state: { categoryData: category || categoryData }
    });
  };

  const handleEditSubCategory = (subCategory) => {
    navigate(`/home/product/categories/subcategories/edit/${categoryIdFromParams}/${subCategory.id}`, {
      state: { 
        categoryData: category || categoryData,
        subCategoryData: subCategory,
        categoryName: category?.name || categoryData?.name,
        subCategoryName: subCategory.name
      }
    });
  };

  const handleDeleteSubCategory = (subCategory) => {
    if (window.confirm(`Are you sure you want to delete "${subCategory.name}"?`)) {
      props.getDataFromAPI(
        `/partner/api/v1/product-categories/${categoryIdFromParams}/subcategories/${subCategory.id}`,
        "DELETE",
        undefined,
        (response) => {
          toastr.success("Success", "Sub category deleted successfully!");
          fetchSubCategories();
        },
        (err) => {
          console.error("Error deleting sub category:", err);
          toastr.error("Failed", err?.message || "Unable to delete sub category");
        }
      );
    }
  };

  // ✅ FIX: Use React Router navigate instead of window.location.href
  const handleViewAttributes = () => {
    const currentCategory = category || categoryData;
    const categoryName = currentCategory?.name || "Category";
    const categoryIdValue = categoryIdFromParams || currentCategory?.id;
    
    console.log("🔄 Navigating to Category Attributes with:", {
      categoryId: categoryIdValue,
      categoryName: categoryName
    });
    
    navigate(`/home/product/categories/attributes/${categoryIdValue}`, {
      state: {
        categoryData: currentCategory,
        categoryName: categoryName,
        categoryId: categoryIdValue
      }
    });
  };

  const handleSubCategoryClick = (subCategory) => {
    navigate(`/home/product/categories/subcategories/attributes/${subCategory.id}`, {
      state: { 
        categoryData: category || categoryData,
        subCategoryData: subCategory,
        categoryName: category?.name || categoryData?.name,
        subCategoryName: subCategory.name
      }
    });
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const getFilteredSubCategories = () => {
    if (!searchTerm.trim()) {
      return subCategories;
    }
    return subCategories.filter(sub =>
      sub.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredSubCategories = getFilteredSubCategories();
  const currentCategory = category || categoryData;

  if (error) {
    return (
      <div className="subcategory-container">
        <Sidebar />
        <div className="subcategory-content">
          <div className="subcategory-error">
            <div className="error-icon">⚠️</div>
            <p>{error}</p>
            <button className="subcategory-retry-btn" onClick={fetchSubCategories}>
              Retry
            </button>
            <button className="subcategory-back-btn" onClick={handleBack}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SubCategoryListComponent
      subCategories={filteredSubCategories}
      category={currentCategory}
      loading={loading}
      searchTerm={searchTerm}
      onSearch={handleSearch}
      onAddSubCategory={handleAddSubCategory}
      onEditSubCategory={handleEditSubCategory}
      onDeleteSubCategory={handleDeleteSubCategory}
      onSubCategoryClick={handleSubCategoryClick}
      onBack={handleBack}
      onRefresh={fetchSubCategories}
      onViewAttributes={handleViewAttributes}
    />
  );
};

function mapStateToProps(state) {
  return {
    props: state,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(SubCategoryListContainer);